import { Formik, getIn } from 'formik';
import React, { ReactNode } from 'react';
import { Form, Modal, Button, Alert } from 'react-bootstrap';
import { useMutation } from '@apollo/client';

import { ContentSourceFormValidation } from '~/components/ContentSources/ContentSourceForm';

import './_ContentSourcesCreateSource.scss';

import { getGraphQLErrorMessages, BasicApolloError } from '~/util/errors';
import createSourceQuery from './_ContentSourcesCreateSource.gql';
import { ContentSourceLink, getContentSourceUrl } from '~/components/ContentSourceLink/ContentSourceLink';
import { useHistory } from 'react-router';
import { CreateAudioFileSourceInput } from '~/graphqlTypes/globalTypes';
import { CreateAudioFileSource } from '~/graphqlTypes/CreateAudioFileSource';

export interface ContentSourcesCreateSourceProps {
  children?: ReactNode;
  className?: string;
  show: boolean;
  onHide: () => void;
}

export const _ContentSourcesCreateSource: React.FC<ContentSourcesCreateSourceProps> = ( props ) => {
  const { show, onHide } = props;
  const history = useHistory();
  const initialValues: CreateAudioFileSourceInput = {
    name: '',
    ddexPartyId: '',
  };

  const [createSource, { data, loading, error, reset: resetMutation }] =
    useMutation<CreateAudioFileSource>( createSourceQuery );
  const allErrors = getGraphQLErrorMessages( error as unknown as BasicApolloError, data, ['all'] );
  const showError = !loading && Boolean( allErrors.length );
  const createdSource = data?.createAudioFileSource;

  return (
    // Change div to the most semantically accurate element for your component
    <div data-testid="ops-content-sources__create-source">
      <Formik
        initialValues={ initialValues }
        validationSchema={ ContentSourceFormValidation }
        onSubmit={ ( audioFileSource ) => createSource( { variables: { audioFileSource } } ) }
      >
        { ( { touched, values, errors, handleChange, handleSubmit, handleBlur, handleReset } ) => (
          <Modal
            as="article"
            data-testid="ops-content-sources__create-source"
            className={ `ops-content-sources-create-source${props.className ? ` ${props.className}` : ''}` }
            show={ show }
            onHide={ onHide }
            size="lg"
          >
            <Form onSubmit={ handleSubmit }>
              <Modal.Header closeButton>
                <Modal.Title as="h4">Create Content Source</Modal.Title>
              </Modal.Header>
              { /* <p>{ getIn( errors, 'name' ) }</p> */ }
              <Modal.Body className="ops-content-sources-create-source__modal-body">
                { loading && (
                  <Alert variant="info">
                    <Alert.Heading>Please Wait</Alert.Heading>
                    <div>Talking to the server…</div>
                  </Alert>
                ) }
                { showError && (
                  <Alert variant="danger">
                    <Alert.Heading>Error</Alert.Heading>
                    { allErrors.length > 1 ? (
                      <ul>
                        { allErrors.map( ( message, index ) => (
                          <li key={ index }>{ message }</li>
                        ) ) }
                      </ul>
                    ) : (
                      <p>{ allErrors[0] }</p>
                    ) }
                  </Alert>
                ) }
                { createdSource && (
                  <Alert variant="success">
                    <Alert.Heading>
                      Created{ ' ' }
                      <ContentSourceLink reference={ { id: createdSource.id } }>{ createdSource.name }</ContentSourceLink>{ ' ' }
                      { values.ddexPartyId && (
                        <>
                          &lt;
                          <code>{ values.ddexPartyId }</code>
                          &gt;
                        </>
                      ) }
                    </Alert.Heading>
                    <Button onClick={ () => history.push( getContentSourceUrl( createdSource.id ) ) }>
                      Go to Source
                    </Button>{ ' ' }
                    <Button
                      onClick={ () => {
                        handleReset();
                        resetMutation();
                      } }
                    >
                      Add Another
                    </Button>
                  </Alert>
                ) }
                { !createdSource && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label id="sourceName">Source Name</Form.Label>
                      <Form.Control
                        placeholder='a short alias (e.g., "WMG")'
                        type="text"
                        name="name"
                        value={ values.name }
                        onChange={ handleChange }
                        onBlur={ handleBlur }
                        isInvalid={ getIn( touched, 'name' ) && Boolean( getIn( errors, 'name' ) ) }
                        disabled={ loading }
                      />
                      { errors && <Form.Control.Feedback type="invalid">{ getIn( errors, 'name' ) }</Form.Control.Feedback> }
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label id="ddexPartyId">
                        DDEX Party ID
                        { ' ' }
                        <Form.Text className="d-inline text-muted">Omit for non-DDEX content source</Form.Text>
                      </Form.Label>
                      <Form.Control
                        placeholder='no dashes or spaces (e.g., "PADPIDA3897722461G")'
                        type="text"
                        name="ddexPartyId"
                        value={ values.ddexPartyId || '' }
                        onChange={ handleChange }
                        onBlur={ handleBlur }
                        isInvalid={ getIn( touched, 'ddexPartyId' ) && Boolean( getIn( errors, 'ddexPartyId' ) ) }
                        disabled={ loading }
                      />
                      { errors && (
                        <Form.Control.Feedback type="invalid">{ getIn( errors, 'ddexPartyId' ) }</Form.Control.Feedback>
                      ) }
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label id="">
                        Price per play
                        { ' ' }
                        <Form.Text className="d-inline text-muted">(USD)</Form.Text>
                      </Form.Label>
                      <Form.Control
                        placeholder='0.00000000'
                        type="number"
                        name="pricePerPlay"
                        min="0"
                        step='0.00000001'
                        onChange={ handleChange }
                        onBlur={ handleBlur }
                        isInvalid={ getIn( touched, 'ddexPartyId' ) && Boolean( getIn( errors, 'ddexPartyId' ) ) }
                        disabled={ loading }
                      />
                    </Form.Group>
                  </>
                ) }
              </Modal.Body>
              <Modal.Footer className="ops-content-sources-create-source__footer">
                <Button type="submit" variant="primary" disabled={ loading || createdSource !== undefined }>
                  Create
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        ) }
      </Formik>
    </div>
  );
};

_ContentSourcesCreateSource.displayName = '_ContentSourcesCreateSource';
